import React, { useEffect, useState } from "react";
import axios from "axios";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import "chart.js/auto";

import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

export default function Analytics() {
  const skin = useSkin();

  const [stats, setStats] = useState({});
  const [dailyData, setDailyData] = useState([]);
  const [guardPerformance, setGuardPerformance] = useState([]);
  const [hourlyDistribution, setHourlyDistribution] = useState([]);
  const [incidentTrend, setIncidentTrend] = useState([]);
  const [timeRange, setTimeRange] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${API_BASE_URL}/reports/analytics?time_range=${timeRange}`
      );

      setStats(res.data.stats || {});
      setDailyData(res.data.daily_data || []);
      setGuardPerformance(res.data.guard_performance || []);
      setHourlyDistribution(res.data.hourly_distribution || []);
      setIncidentTrend(res.data.incident_trend || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const patrolTrendChart = {
    labels: dailyData.map((d) => formatDate(d.date)),
    datasets: [
      {
        label: "Patrols",
        data: dailyData.map((d) => d.patrol_count),
        borderColor: "#3b82f6",
        backgroundColor: "rgba(59,130,246,0.2)",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const guardChart = {
    labels: guardPerformance.map((d) => `Guard ${d.guard_id}`),
    datasets: [
      {
        label: "Patrols Completed",
        data: guardPerformance.map((d) => d.patrol_count),
        backgroundColor: "#22c55e",
      },
    ],
  };

  const hourlyChart = {
    labels: hourlyDistribution.map((d) => `${d.hour}:00`),
    datasets: [
      {
        label: "Patrols by Hour",
        data: hourlyDistribution.map((d) => d.patrol_count),
        backgroundColor: "#f97316",
      },
    ],
  };

  const incidentTrendChart = {
    labels: incidentTrend.map((d) => formatDate(d.date)),
    datasets: [
      {
        label: "Incidents",
        data: incidentTrend.map((d) => d.incident_count),
        borderColor: "#ef4444",
        backgroundColor: "rgba(239,68,68,0.2)",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const patrolVsIncidentChart = {
    labels: ["Patrols", "Incidents"],
    datasets: [
      {
        data: [stats.total_patrols || 0, stats.total_incidents || 0],
        backgroundColor: ["#3b82f6", "#ef4444"],
      },
    ],
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading analytics...
      </div>
    );
  }

  return (
    <Layout activePage="analytics">
      <PageHeader />

      <FormSection title="Analytics Filters">
        <div className="grid md:grid-cols-4 gap-4">
          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(Number(e.target.value))}
          >
            <option value={7}>7 Days</option>
            <option value={30}>30 Days</option>
            <option value={60}>60 Days</option>
          </Select>

          <button
            onClick={fetchAnalytics}
            className="px-4 py-3 rounded-xl text-white"
            style={{ background: skin.gradient }}
          >
            Refresh
          </button>
        </div>
      </FormSection>

      <FormSection title="Overview">
        <div className="grid md:grid-cols-4 gap-4">
          <Stat label="Guards" value={stats.total_guards || 0} />
          <Stat label="Patrols" value={stats.total_patrols || 0} />
          <Stat label="Incidents" value={stats.total_incidents || 0} />
          <Stat label="Completion %" value={`${stats.completion_rate || 0}%`} />
        </div>
      </FormSection>

      <div className="grid lg:grid-cols-2 gap-6">
        <ChartCard
          title="Daily Patrol Trend"
          description="Shows how many patrol scans were completed each day."
        >
          <Line data={patrolTrendChart} />
        </ChartCard>

        <ChartCard
          title="Incident Trend"
          description="Shows incident volume during the selected period."
        >
          <Line data={incidentTrendChart} />
        </ChartCard>

        <ChartCard
          title="Top Guard Performance"
          description="Top 10 guards based on completed patrol scans."
        >
          <Bar data={guardChart} />
        </ChartCard>

        <ChartCard
          title="Hourly Patrol Distribution"
          description="Helps identify peak patrol activity hours."
        >
          <Bar data={hourlyChart} />
        </ChartCard>

        <ChartCard
          title="Patrols vs Incidents"
          description="Quick comparison between total patrol activity and incidents."
        >
          <div className="max-w-sm mx-auto">
            <Doughnut data={patrolVsIncidentChart} />
          </div>
        </ChartCard>

        <DetailsCard
          title="Analytics Summary"
          stats={stats}
          guardPerformance={guardPerformance}
          hourlyDistribution={hourlyDistribution}
        />
      </div>
    </Layout>
  );
}

/* ================= UI COMPONENTS ================= */

function PageHeader() {
  return (
    <div className="mb-6">
      <h2 style={{ fontFamily: FONT_HEADING }}>Analytics Dashboard</h2>
      <p style={{ fontFamily: FONT_UI }}>
        Visual insights of patrol performance, incidents, guards and trends
      </p>
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <div className="bg-white p-6 rounded-2xl mb-6">
      <h3 className="mb-4" style={{ fontFamily: FONT_HEADING }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function ChartCard({ title, description, children }) {
  return (
    <div className="bg-white p-6 rounded-2xl mb-6">
      <h3 className="mb-1" style={{ fontFamily: FONT_HEADING }}>
        {title}
      </h3>
      <p className="text-sm text-slate-500 mb-4">{description}</p>
      {children}
    </div>
  );
}

function DetailsCard({ title, stats, guardPerformance, hourlyDistribution }) {
  const topGuard = guardPerformance[0];
  const peakHour = hourlyDistribution.reduce(
    (max, item) => (item.patrol_count > max.patrol_count ? item : max),
    { patrol_count: 0 }
  );

  return (
    <div className="bg-white p-6 rounded-2xl mb-6">
      <h3 className="mb-4" style={{ fontFamily: FONT_HEADING }}>
        {title}
      </h3>

      <div className="space-y-3 text-sm text-slate-700">
        <p>
          Total patrols completed:{" "}
          <strong>{stats.total_patrols || 0}</strong>
        </p>

        <p>
          Total incidents reported:{" "}
          <strong>{stats.total_incidents || 0}</strong>
        </p>

        <p>
          Completion rate:{" "}
          <strong>{stats.completion_rate || 0}%</strong>
        </p>

        <p>
          Best performing guard:{" "}
          <strong>
            {topGuard ? `Guard ${topGuard.guard_id}` : "No data"}
          </strong>
        </p>

        <p>
          Peak patrol hour:{" "}
          <strong>
            {peakHour.hour !== undefined ? `${peakHour.hour}:00` : "No data"}
          </strong>
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="p-4 bg-slate-50 rounded-xl">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="text-xl font-bold">{value}</div>
    </div>
  );
}

function Select(props) {
  return <select {...props} className="border p-3 rounded-xl w-full" />;
}

function formatDate(date) {
  if (!date) return "";
  return new Date(date).toLocaleDateString();
}